// v6 F13 §7-§8 — Help & handleiding pagina (finale tekstpolish).
// Alle FAQ- en topic-teksten volgen de door de gebruiker aangeleverde copy:
// eenvoudige praktijktaal, geen technische termen, knopnamen exact zoals in
// de interface, geen beloften over nog-niet-live functies (offline/PWA/
// digitale handtekening/handtekeningblok).

import React, { useState } from "react";
import { Link } from "react-router-dom";
import {
  BookOpen, FilePlus2, Users, Home, Building2, ArrowUp, Wrench,
  Layers, Package, FileDown, HelpCircle, ChevronDown, ArrowLeft, Search,
} from "lucide-react";
import { Input } from "../components/ui/input";

// v6 F35-refresh — Categorie-indeling voor Help/FAQ.
// Bestaande topic-ID's behouden zodat oude links niet breken.
const CATEGORIES = [
  { id: "aan-de-slag",     label: "Aan de slag" },
  { id: "meten",           label: "Meten & werkzones" },
  { id: "prijzen",         label: "Prijzen & sjablonen" },
  { id: "offertes",        label: "Offertes" },
  { id: "facturen",        label: "Facturen" },
  { id: "btw",             label: "BTW & fiscale regels" },
  { id: "internationaal",  label: "Internationaal" },
  { id: "instellingen",    label: "Instellingen & E-mail" },
  { id: "ai-assistent",    label: "AI Offerte Assistent" },
];

const TOPICS = [
  {
    id: "eerste-offerte",
    category: "aan-de-slag",
    icon: FilePlus2,
    title: "Eerste offerte maken",
    desc: "Van klant en project naar een eerste offerte.",
    steps: [
      "Ga naar 'Klanten' en voeg je klant toe met naam, adres en e-mail.",
      "Voeg onder die klant een project toe. Vul een projectnaam en werfadres in.",
      "Open het project en klik bovenaan op 'Nieuwe' om een nieuwe offerte te starten.",
      "Vul onder 'Offerte details' de titel, omschrijving en eventuele opmerkingen in.",
      "Voeg ruimtes of werkzones toe via 'Ruimte toevoegen' en 'Werkzone toevoegen'.",
      "Klik op 'PDF' om de offerte te genereren en te delen met je klant.",
    ],
  },
  {
    id: "klanten-projecten",
    category: "aan-de-slag",
    icon: Users,
    title: "Klanten en projecten",
    desc: "Beheer je klantenbestand en projecten per klant.",
    steps: [
      "Elke klant heeft een naam, contactgegevens en desgewenst een BTW-nummer.",
      "Per klant kan je meerdere projecten aanmaken, elk met een eigen werfadres.",
      "Elk project heeft zijn eigen lijst met offertes.",
    ],
  },
  {
    id: "binnenzones",
    category: "meten",
    icon: Home,
    title: "Binnenzones — Woonruimte en andere ruimtes",
    desc: "Kies een ruimtetype en meet snel op.",
    steps: [
      "Klik 'Ruimte toevoegen' en kies een type: Woonruimte, Kantoor, Magazijn, Industriële ruimte, Publieke ruimte, Andere ruimte of Algemeen.",
      "Nieuwe ruimtes krijgen automatisch een naam zoals 'Woonruimte 1', 'Woonruimte 2'. Klik op de naam om vrij te hernoemen — de volledige naam is meteen geselecteerd.",
      "Vul lengte, breedte en hoogte in. Muur- en plafondoppervlaktes worden automatisch berekend.",
      "Vink de werken aan die je wil uitvoeren. Hoeveelheden lopen mee met je afmetingen.",
      "Voeg deuren en ramen toe als aftrek van de wandoppervlakte.",
      "Wil je per muur werken? Kies 'Vrije opmeting' om aparte muur- en plafondvlakken toe te voegen.",
    ],
  },
  {
    id: "buitenzones",
    category: "meten",
    icon: Building2,
    title: "Buitenzones — Woning, Chalet, Bijgebouw…",
    desc: "Kies een gebouwtype en meet je gevels op.",
    steps: [
      "Klik 'Werkzone toevoegen' en kies bijvoorbeeld Woning, Chalet, Bijgebouw, Bedrijfsgebouw of Tuinmuur.",
      "De zone krijgt automatisch een naam zoals 'Woning 1'. Klik erop om te hernoemen — alles is meteen geselecteerd.",
      "Voeg één of meerdere gevels toe met de knop '+ Gevelvlak toevoegen'. Ze worden automatisch genummerd (Gevelvlak 1, Gevelvlak 2, …) per gebouwzone.",
      "Vul per gevel de afmetingen in (rechthoek, driehoek of gemengd). Trek deuren en ramen af.",
      "Elke gebouwzone gebruikt één gevelsjabloon. Zijn er meerdere geldige sjablonen, kies dan de juiste in het dropdown. Is er slechts één sjabloon (bijvoorbeeld bij Trap), wordt die meteen automatisch geselecteerd.",
      "De hoeveelheden lopen automatisch mee met de netto geveloppervlakte.",
    ],
  },
  {
    id: "buitenelementen",
    category: "meten",
    icon: Package,
    title: "Zelfstandige Buitenelementen-zone",
    desc: "Dakoversteken, boeiboorden, kroonlijsten, raamlijsten en dorpels — apart opgemeten.",
    steps: [
      "Klik 'Werkzone toevoegen' en kies 'Buitenelementen'. Deze zone bevat geen gevelvlakken; enkel losse elementen.",
      "Voeg elementen toe: kies type (Dakoversteek, Boeiboord, Kroonlijst, Raamlijst, Dorpel of Ander) + materiaal (Hout, Metaal, PVC, Beton/Steen, Composiet).",
      "Elk element wordt individueel opgemeten in m², lopende meter, stuks of forfait.",
      "Elementen groeperen automatisch op type + materiaal. Bv. drie hout-boeiboorden komen samen in één 'Boeiboord + Hout'-groep met gedeelde werkposten.",
      "Bij hout-elementen kan je één eindafwerking kiezen: beitsen, vernissen of schilderen/lakken (SCHUURGRAAD — KIES ÉÉN). Voorbereidende werken (reinigen, schuren, primer) blijven combineerbaar.",
      "Wijzig je het materiaal van één element? Dan verhuist het automatisch naar de juiste nieuwe groep.",
    ],
  },
  {
    id: "trap",
    category: "meten",
    icon: ArrowUp,
    title: "Trap toevoegen",
    desc: "Aparte werkzone voor een trap met eigen meetlogica.",
    steps: [
      "Klik 'Werkzone toevoegen' en kies 'Trap'.",
      "Het sjabloon 'EMMA™ — Trapwerken' wordt meteen automatisch geselecteerd — je ziet de werkposten direct verschijnen zonder extra klik.",
      "Vul het aantal treden, de breedte, de aantrede en de optrede in.",
      "Kies of het een open of gesloten trap is.",
      "Werken per trede (schuren, schilderen) krijgen automatisch de juiste hoeveelheid.",
      "Werken in lopende meter, zoals trapwand of leuning, vragen een aparte lengte.",
      "Voor m²-werken, zoals de onderzijde van de trap, kan je 'Gebruik trapoppervlakte' aanvinken.",
    ],
  },
  {
    id: "schrijnwerk",
    category: "meten",
    icon: Wrench,
    title: "Schrijnwerk toevoegen",
    desc: "Ramen, deuren, luiken meten en behandelen.",
    steps: [
      "Klik 'Werkzone toevoegen' en kies 'Binnenschrijnwerk' of 'Buitenschrijnwerk'.",
      "Voeg de objecten toe (raam, deur, luik, schuifraam, poort) met lengte en hoogte per stuk. Schuifraam is een APART objecttype geworden — bij Buitenschrijnwerk kan je 'Schuifraam' direct kiezen; geen aparte toggle meer nodig.",
      "Kies het bijhorende sjabloon: basisafwerking of decoratieve afwerking. Ook lm-schuurregels (S-06.46 Buitenkaders schuren licht €3/lm · S-06.47 Buitenkaders schuren intensief €5,50/lm) zitten standaard in de buitenschrijnwerk-sjablonen.",
      "Werken uit het sjabloon rekenen automatisch met de gekozen meetmethode (m² of lm — zie onderwerp 'Meetmethode m² vs lm').",
    ],
  },
  {
    id: "meetmethode-m2-lm",
    category: "meten",
    icon: Wrench,
    title: "Meetmethode — Oppervlak m² vs Kaderwerk lm",
    desc: "Kies per raam of deur hoe je meet: op basis van oppervlakte of kaderwerk.",
    steps: [
      "Bij elk schrijnwerk-object (raam, deur, schuifraam) zie je bovenaan een keuzeschakelaar met twee knoppen: 'Oppervlak m²' en 'Kaderwerk lm'.",
      "Oppervlak m² — voor schilderen/spuiten/beitsen/vernissen van volledige raam- of deuroppervlakken. Berekent B × H × aantal × zijden en toont m² onder 'Behandelend oppervlak'. Constructievelden (vaste/bewegende delen, middenstijl) zijn dan niet nodig en worden verborgen.",
      "Kaderwerk lm — voor het schilderen van de omtrek/kaders. Berekent de volledige raamconstructie in lopende meter (buitenkader + interne verticalen + bewegende vleugels + eventueel bovenlicht). Toont 0 m² en de berekende lm.",
      "Wisselen kan direct via de segmented control. De berekening en de zichtbare werkregels vernieuwen in één keer — geen dubbele klik nodig.",
      "In m²-modus zie je enkel de m²-werkregels (bv. S-06.40 Lakken m², S-06.41 Beitsen m²). In lm-modus zie je enkel de lm-kaderregels (bv. S-06.43/44/45 Kaders lakken/beitsen/vernissen, S-06.46/47 Kaders schuren). Algemene items zoals 'Beschermen en afplakken' blijven altijd zichtbaar.",
    ],
  },
  {
    id: "schuifraam-kruisverdeling",
    category: "meten",
    icon: Wrench,
    title: "Schuifraam en kruisverdeling",
    desc: "Speciale raamtypes met eigen berekening.",
    steps: [
      "Schuifraam is een apart objecttype in Buitenschrijnwerk. Bij toevoegen kies je 'Schuifraam' en vul je breedte, hoogte, aantal en de verhouding vast/schuivend in.",
      "Voor een schuifraam in Kaderwerk lm-modus wordt automatisch één vast + één schuivend paneel gerekend, met breedte volgens de gekozen split-ratio.",
      "Kruisverdeling (klassiek raam met een kruis in het midden) is onder 'Meer opties' beschikbaar. Bij kruisverdeling wordt het volledige raamoppervlak in m² behandeld (bewust geen individuele latten).",
      "Middenstijl aanwezig? — Voor nieuwe ramen staat deze default op 'Nee' (geen middenstijl). Wijzig via de Ja/Nee-toggle als je raam wel een middenstijl heeft.",
    ],
  },
  {
    id: "zijde-schrijnwerk",
    category: "meten",
    icon: Wrench,
    title: "Zijde-logica Binnen vs Buitenschrijnwerk",
    desc: "Wanneer wordt de binnen- of buitenzijde meegerekend?",
    steps: [
      "Bij Buitenschrijnwerk staat elk object standaard op 'Buitenzijde' — de buitenzijde wordt altijd behandeld.",
      "Vink 'Ook binnenzijde behandelen' aan om beide zijden mee te rekenen. De hoeveelheid verdubbelt automatisch (sides-multiplier ×2) voor ramen, deuren én kruisverdelingen.",
      "Bij Binnenschrijnwerk is er GEEN zijde-keuze: alleen de binnenzijde wordt behandeld. Dit is de correcte werkelijkheid voor bijvoorbeeld binnendeuren.",
      "Garagepoorten en buitenrolluiken zijn enkel zichtbaar in Buitenschrijnwerk-zones.",
    ],
  },
  {
    id: "sjablonen",
    category: "prijzen",
    icon: Layers,
    title: "Sjablonen — inclusief spuitwerk",
    desc: "Bundels van standaardwerken per ruimte of zone.",
    steps: [
      "Een sjabloon bundelt de werken die vaak samengaan, bijvoorbeeld schuren, plamuren en twee lagen verf.",
      "Voor binnenruimtes zijn er onder meer: EMMA™ — Binnen schilderwerken, EMMA™ — Behangwerken, EMMA™ — Decoratieve technieken en EMMA™ — Wanden en plafonds spuiten.",
      "Het spuitsjabloon 'Wanden en plafonds spuiten' bevat een volwaardige werkvolgorde: voorbereiding (afplakken, vloer beschermen, reinigen, herstelwerk, schuren), muren spuiten (primer, 2 afwerklagen, extra laag), plafond spuiten (idem) en aanvullende werken (apparatuur, nacontrole, stelling).",
      "Voor hout-eindafwerking geldt 'SCHUURGRAAD — KIES ÉÉN': slechts één van licht schuren of intensief schuren tegelijk selecteerbaar.",
      "Aangevinkte werken krijgen automatisch de juiste hoeveelheid in m², lopende meter, stuks of forfait.",
      "Je ziet alleen sjablonen die passen bij de gekozen ruimte of werkzone. Werkzone-specifieke sjablonen (Trap, Meerwerken, Schrijnwerk) verschijnen niet in de ruimte-picker en omgekeerd.",
    ],
  },
  {
    id: "prijzen-aanpassen",
    category: "prijzen",
    icon: Package,
    title: "Prijzen aanpassen per offerte",
    desc: "Overschrijf een startprijs zonder de centrale prijslijst te wijzigen.",
    steps: [
      "Klik op de prijs naast een aangevinkte werkpost. Er verschijnt een invoerveld — tik je nieuwe prijs.",
      "Zodra je een andere prijs invoert, verschijnt de badge 'AANGEPAST' naast de regel.",
      "Deze aanpassing geldt uitsluitend voor deze offerte. De centrale prijslijst (Prijslijst-pagina) blijft ongewijzigd.",
      "Op de Prijslijst-pagina kan je wél de centrale startprijs voor nieuwe offertes wijzigen. Bestaande offertes gebruiken hun eigen historische vaste kopie en veranderen niet mee — belangrijk voor commerciële continuïteit.",
      "Regel-, zone- en eindtotalen worden onmiddellijk herberekend.",
      "Vink je een regel uit en later opnieuw aan? De vorige aanpassing komt automatisch terug (met de badge 'AANGEPAST' opnieuw zichtbaar).",
      "Dit werkt uniform in alle blokken: Woonruimte en andere ruimtes, gevelsjablonen, buitenschrijnwerk, binnenschrijnwerk, buitenelementen-groepen en trapwerken.",
    ],
  },
  {
    id: "omschrijving-per-werkregel",
    category: "prijzen",
    icon: FilePlus2,
    title: "Omschrijving per werkregel aanpassen",
    desc: "Overschrijf de tekst van een werkregel zonder de prijslijst te raken.",
    steps: [
      "Naast elke werkregel zie je de omschrijving-tekst inline (geen aparte edit-modus, geen potloodicoon).",
      "Klik in het tekstveld en typ je aangepaste tekst. Bij focus buiten het veld wordt automatisch opgeslagen.",
      "Zodra je een andere tekst invoert, verschijnt de badge 'AANGEPAST' + een ↶ Herstel-knop.",
      "Klik op ↶ Herstel om terug te vallen op de standaardtekst (uit de prijslijst) of op de historische vaste kopie van deze offerte.",
      "Standaard staat het tekstveld op 1 regel; bij langere tekst groeit het automatisch naar maximaal 2 regels. Zo blijft de lay-out compact.",
      "Deze offerte-specifieke tekst raakt de centrale prijslijst niet. Andere offertes blijven de originele omschrijving zien.",
    ],
  },
  {
    id: "snapshots-historisch",
    category: "prijzen",
    icon: FileDown,
    title: "Bestaande offertes blijven stabiel",
    desc: "Wijzigingen in de prijslijst raken je verzonden offertes nooit.",
    steps: [
      "Elke offerteregel bewaart bij het aanmaken een vaste kopie van de prijs en de omschrijving. Zo blijft je offerte altijd tonen wat je klant heeft gezien.",
      "Wijzig je later de centrale prijslijst? Bestaande offertes blijven exact zoals ze waren. Enkel nieuwe offertes gebruiken de bijgewerkte prijzen.",
      "Volgorde van weergave: eigen aanpassing op deze offerte → vaste kopie van deze offerte → huidige prijslijst.",
      "Herstel je een aanpassing (via ↶)? Dan valt de regel terug op de vaste kopie van deze offerte — niet op de huidige prijslijst. Zo blijft je offerte commercieel stabiel.",
    ],
  },
  {
    id: "ai-assistent",
    category: "ai-assistent",
    icon: HelpCircle,
    title: "Emma — AI Offerte Assistent",
    desc: "Laat Emma je offerte-teksten voorstellen en controleren.",
    steps: [
      "Open 'Offerte details' en klik het paneel 'Emma — AI Offerte Assistent' open.",
      "Optioneel: vul 'Extra context voor Emma' in met projectspecifieke waarheden zoals 'Behangpapier wordt geleverd door klant' of 'Alleen één wand wordt behangen'. Emma respecteert deze tekst letterlijk als projectwaarheid.",
      "Klik 'Genereer projectomschrijving'. Emma stelt titel, korte omschrijving, 'De werken omvatten' en opmerkingen/uitsluitingen voor — uitsluitend op basis van de geselecteerde werken, ruimtes en de extra context. Ze verzint niets.",
      "De voorstellen verschijnen in een preview. Je kan per veld 'Overnemen' klikken of 'Accepteer alles'. Niets wordt gewijzigd tot je klikt.",
      "Bij accepteren van de titel vraagt Emma of je ook de Projectnaam wil bijwerken — enkel na expliciete bevestiging. Indien projectnaam en offertetitel identiek zijn, toont de PDF geen dubbele titel.",
      "Elke accept-actie maakt een Undo-entry aan. Klik ↶ Ongedaan maken om de AI-tekst terug te draaien naar je vorige versie.",
      "Klik 'Regenereer' om een nieuw voorstel te vragen (met evt. aangepaste extra context). 'Sluit preview' verwerpt het voorstel volledig.",
      "Wijzig je na een AI-generatie de offerte (bv. een ruimte toevoegen)? Emma toont een subtiele melding 'Offerte gewijzigd — projectomschrijving opnieuw genereren'. Er wordt NIETS automatisch geregenereerd; jij beslist wanneer je opnieuw klikt.",
    ],
  },
  {
    id: "autosave-reload",
    category: "offertes",
    icon: FileDown,
    title: "Autosave, reload en mobiel meten",
    desc: "Je werk gaat nooit verloren.",
    steps: [
      "Alles wat je invoert wordt automatisch bewaard — geen 'Opslaan'-knop nodig.",
      "Sluit je het venster of laad je de pagina opnieuw? Je vindt de offerte exact zoals je hem verliet, inclusief overrides en 'AANGEPAST'-badges.",
      "Op mobiel meet je snel ter plekke: hoeveelheden, prijzen en regeltotalen blijven leesbaar per rij. Prijzen aanpassen doe je op desktop of tablet in een breder invoerveld.",
      "Namen die automatisch gegenereerd zijn (Woonruimte 2, Gevel 3, Boeiboord 1, …) worden bij eerste focus volledig geselecteerd, zodat één toets de hele naam overschrijft.",
    ],
  },
  {
    id: "extra-posten",
    category: "offertes",
    icon: Package,
    title: "Extra posten, opties en kortingen",
    desc: "Projectbrede extra's, opties, forfaits en kortingen.",
    steps: [
      "Onderaan de offerte vind je de sectie 'Extra posten en opties'.",
      "Voeg extra's toe zoals transport, opruim of werfkosten.",
      "Opties worden apart getoond en tellen niet mee in het hoofdtotaal.",
      "Voeg kortingen toe als percentage of vast bedrag.",
      "Kortingen worden automatisch correct verdeeld volgens het juiste BTW-tarief.",
    ],
  },
  {
    id: "pdf-instellingen",
    category: "offertes",
    icon: FileDown,
    title: "PDF, Offerte Preview en instellingen",
    desc: "Pas de weergave van je offerte aan.",
    steps: [
      "Klik op 'Preview' bovenaan om de offerte te bekijken zoals je klant ze zal krijgen.",
      "Editorbedrag, Offerte Preview en PDF-bedrag zijn altijd identiek — er is geen verschil tussen wat je ziet en wat je klant krijgt.",
      "'Eenheidsprijzen tonen': toon of verberg de prijs per eenheid op de offerte.",
      "'BTW-percentage tonen per regel': toon per regel het BTW-tarief. Handig wanneer je verschillende tarieven gebruikt.",
      "'Overzicht per onderdeel': toon een subtotaal per kamer, gevel of werkzone.",
      "'Codes tonen': toon de prijscodes bij de werken op de offerte.",
    ],
  },
  // ────────────────────────────────────────────────────────────────
  // v6 F35-refresh — Nieuwe topics: Facturen, BTW, Internationaal, Instellingen
  // ────────────────────────────────────────────────────────────────
  {
    id: "factuur-concept-vs-definitief",
    category: "facturen",
    icon: FileDown,
    title: "Concept- en definitieve factuur",
    desc: "Zolang een factuur concept is kan je alles nog aanpassen.",
    steps: [
      "Een nieuwe factuur start altijd als 'Concept'. Je kan lijnen, bedragen, BTW-regime en notities vrij aanpassen.",
      "Het factuurnummer wordt pas toegekend wanneer je op 'Definitief maken' klikt — zo blijft je nummering ononderbroken, ook als je een concept later wist.",
      "Bij het definitief maken wordt het BTW-regime en de wettelijke tekst vastgelegd. De factuur is dan onveranderlijk en kan verzonden worden.",
      "Een definitieve factuur kan later gemarkeerd worden als 'Verzonden' of 'Betaald'.",
    ],
  },
  {
    id: "factuur-typen",
    category: "facturen",
    icon: FileDown,
    title: "Voorschot, tussenfactuur, eindfactuur en vrije factuur",
    desc: "Verschillende factuurtypes uit één offerte of los.",
    steps: [
      "Voorschotfactuur: het eerste deel van de opdracht, doorgaans vóór start van de werken.",
      "Tussenfactuur: tijdens de uitvoering, voor een tussenfase.",
      "Eindfactuur: sluit het project af. Deze factureert altijd exact het resterend factureerbaar saldo, ongeacht welk percentage je invulde. Zo krijg je nooit overfacturatie of restsaldo.",
      "Vrije factuur: een factuur zonder gekoppelde offerte, bijvoorbeeld voor een kleine bijkomende opdracht.",
      "Zolang de factuur concept is kan je het bedrag, de omschrijving, het BTW-regime en de notitie aanpassen.",
    ],
  },
  {
    id: "factuurnummering",
    category: "facturen",
    icon: FileDown,
    title: "Factuurnummering instellen",
    desc: "Kies je prefix, jaartal en start-nummer.",
    steps: [
      "Ga naar Instellingen → Facturen & betalingen → Factuurnummering.",
      "Bovenaan zie je live het nummer dat de eerstvolgende factuur zal krijgen, bijvoorbeeld 'FAC-2026-127'.",
      "Voorvoegsel: de letters vóór het nummer (bv. FAC of INV).",
      "Volgend nummer: start-nummer. Begin je EmmaMeasure midden in het jaar te gebruiken? Zet hier bijvoorbeeld 127 zodat je vloeiend verder telt op je bestaande boekhouding.",
      "Jaartal in nummer: aan/uit — voegt het huidige jaar toe (bv. FAC-2026-127).",
      "Jaarlijks resetten: aan/uit — telling begint elk jaar opnieuw op 1.",
    ],
  },
  {
    id: "factuur-betalingsgegevens",
    category: "facturen",
    icon: FileDown,
    title: "Betalingsgegevens en deelfacturatie",
    desc: "Vervaltermijn, IBAN, BIC, referentie en standaardpercentages.",
    steps: [
      "Ga naar Instellingen → Facturen & betalingen.",
      "Vervaltermijn: standaard aantal dagen tot de vervaldatum. Wordt berekend bij het definitief maken.",
      "IBAN + BIC: verschijnen automatisch op elke factuur.",
      "Betalingsreferentie-sjabloon: bijvoorbeeld 'Factuur {invoice_number}'. Wordt per factuur automatisch ingevuld.",
      "Standaard deelfacturatie: percentages voor voorschot, tussenfactuur en eindfactuur die de factuurwizard als voorstel gebruikt. De eindfactuur blijft altijd exact gelijk aan het resterend saldo.",
    ],
  },
  {
    id: "btw-regimes",
    category: "btw",
    icon: HelpCircle,
    title: "BTW-regime kiezen: Standaard, Renovatie 6%, BTW verlegd",
    desc: "Eenvoudige uitleg per regime.",
    steps: [
      "Standaard BTW: gewone BTW-tarieven per lijn (bv. 21% of 6%). Kies dit als je geen bijzondere fiscale regeling toepast.",
      "Renovatie 6%: verlaagd tarief voor werken aan een privéwoning die aan de wettelijke voorwaarden voldoet. EmmaMeasure stelt 6% als standaardtarief voor bij nieuwe lijnen; je kan gemengde tarieven blijven gebruiken.",
      "BTW verlegd: de BTW wordt naar je opdrachtgever verlegd. Op de factuur staat het BTW-bedrag op 0 en verschijnt de vermelding 'BTW verlegd'.",
      "Elke klant heeft een standaard BTW-regime. Dit regime wordt automatisch overgenomen wanneer je een factuur voor die klant aanmaakt.",
      "Zolang een factuur concept is kan je het regime nog wijzigen. Bij het definitief maken wordt het regime vastgelegd en samen met de wettelijke tekst bevroren.",
    ],
  },
  {
    id: "btw-wettelijke-tekst",
    category: "btw",
    icon: HelpCircle,
    title: "Wettelijke tekst en 'nog te bevestigen'",
    desc: "Waarom soms 'Wettelijke tekst nog te bevestigen door je accountant' verschijnt.",
    steps: [
      "Voor Renovatie 6% en BTW verlegd verschijnt een korte juridische vermelding op de factuur.",
      "Zolang je accountant deze tekst niet formeel bevestigd heeft, toont EmmaMeasure een duidelijke melding: 'Wettelijke tekst nog te bevestigen door je accountant'.",
      "Zo weet zowel jij als je klant dat de definitieve wettelijke formulering nog volgt.",
      "Laat je accountant de teksten bevestigen zodat we ze op 'bevestigd' kunnen zetten. Ondertussen verandert er niets aan je BTW-berekening — enkel de tekst zelf.",
    ],
  },
  {
    id: "internationaal",
    category: "internationaal",
    icon: Building2,
    title: "Ondersteunde landen en talen",
    desc: "5 landen, met België meertalig.",
    steps: [
      "EmmaMeasure ondersteunt: 🇧🇪 België, 🇳🇱 Nederland, 🇱🇺 Luxemburg, 🇫🇷 Frankrijk en 🇩🇪 Duitsland.",
      "Land bepaalt de fiscale context (welke BTW-regimes beschikbaar zijn, welke registratienummers).",
      "Taal & regio bepaalt hoe je documenten getoond worden (taal van labels, datum- en getalnotatie).",
      "België ondersteunt Nederlands, Frans en Duits (Oostkantons). Je kiest bij Instellingen → Jouw bedrijf welke documenttaal je wil.",
      "Voor Luxemburg, Frankrijk en Duitsland is de fiscale configuratie voorlopig nog beperkt: enkel 'Standaard BTW' is actief tot een geverifieerde regime-catalog voor dat land beschikbaar is.",
    ],
  },
  {
    id: "instellingen-overzicht",
    category: "instellingen",
    icon: BookOpen,
    title: "Instellingen — de 6 secties",
    desc: "Wat vind je waar?",
    steps: [
      "Jouw bedrijf: bedrijfslogo, naam, land, adres, contactgegevens, fiscaal nummer, documenttaal & regio en valuta.",
      "Offertes: nummering (voorvoegsel, volgend nummer, jaartal) en offerte-defaults (geldigheid in dagen, 'Voor akkoord'-blok, standaard betalingsvoorwaarden).",
      "Facturen & betalingen: factuurnummering, IBAN/BIC/vervaltermijn/betalingsreferentie en standaard deelfacturatie-percentages.",
      "BTW & fiscale regels: overzicht van de beschikbare BTW-regimes voor jouw land (Standaard BTW, Renovatie 6%, BTW verlegd).",
      "E-mail: afzendernaam, reply-to, standaard onderwerp, begeleidend bericht, handtekening en mailbox-koppeling.",
      "Merken & materialen: voorkeuren per categorie (binnenverf, buitenverf, lakwerk, …) die Emma gebruikt in klantteksten.",
    ],
  },
];

const FAQ = [
  {
    category: "meten",
    q: "Wat is het verschil tussen een gebouwzone (Woning/Chalet/…) en een Buitenelementen-zone?",
    a: "Een gebouwzone bevat één of meerdere gevels (rechthoek, driehoek of gemengd) en één gevelsjabloon.\nEen zelfstandige Buitenelementen-zone bevat géén gevels. Ze verzamelt losse elementen (dakoversteek, boeiboord, kroonlijst, raamlijst, dorpel) die per stuk opgemeten worden en automatisch groeperen op type + materiaal.",
  },
  {
    category: "prijzen",
    q: "Waarom is een sjabloon soms meteen gekozen en zie ik geen dropdown?",
    a: "Wanneer er voor een zone slechts één relevant actief sjabloon bestaat (bijvoorbeeld Trap → EMMA™ — Trapwerken), wordt dat sjabloon automatisch geselecteerd bij het aanmaken van de zone. Je ziet dan een 'Auto'-badge met de sjabloonnaam en een '✕ Wis'-knop om alsnog te wissen.",
  },
  {
    category: "meten",
    q: "Kan ik gevels vrij hernoemen?",
    a: "Ja. Nieuwe gevels krijgen automatisch een naam ('Gevelvlak 1', 'Gevelvlak 2', …) per gebouwzone. Klik op de naam om te bewerken — de hele naam is direct geselecteerd zodat één toets de nieuwe naam kan invoeren.",
  },
  {
    category: "prijzen",
    q: "Wat betekent de badge 'AANGEPAST' naast een prijs?",
    a: "Die badge verschijnt zodra je op een offerte een andere eenheidsprijs invoert dan de standaardprijs uit de centrale prijslijst. De aanpassing geldt enkel voor deze offerte. Vink je de regel uit en later opnieuw aan? De aangepaste prijs komt automatisch terug.",
  },
  {
    category: "prijzen",
    q: "Ik wijzig de centrale prijslijst — waarom veranderen mijn oude offertes niet mee?",
    a: "Dat is bewust: elke offerteregel bewaart bij aanmaak een vaste kopie van de prijs en de omschrijving. Zo blijven verstuurde offertes exact zoals de klant ze zag. Nieuwe offertes gebruiken wél de bijgewerkte prijslijst. Wil je toch een oude offerte updaten? Vink de regel uit en opnieuw aan.",
  },
  {
    category: "meten",
    q: "Wanneer moet ik Oppervlak m² kiezen versus Kaderwerk lm?",
    a: "Kies 'Oppervlak m²' als je per vierkante meter aanrekent (bv. lakken/spuiten/beitsen van een raamvlak of deurblad). Kies 'Kaderwerk lm' als je per lopende meter aanrekent (bv. schilderen van kaders/omtrek). De keuze bepaalt welke werkregels zichtbaar zijn en hoe de hoeveelheid berekend wordt.",
  },
  {
    category: "ai-assistent",
    q: "Verzint Emma dingen die niet in mijn offerte staan?",
    a: "Nee. Emma krijgt strikt enkel je geselecteerde werken, ruimtes en de extra context die je zelf invult. De prompt legt op: 'verzin niets buiten deze data'. Werkt de AI toch iets fout? Klik 'Regenereer' of gebruik ↶ Ongedaan maken om terug te draaien.",
  },
  {
    category: "ai-assistent",
    q: "Wat gebeurt er als ik de AI-titel accepteer en die is anders dan mijn Projectnaam?",
    a: "Emma vraagt via een bevestigingsdialoog of je ook de Projectnaam wil bijwerken. Enkel na expliciete bevestiging wijzigt de projectnaam. Zijn Projectnaam en Offertetitel identiek, dan toont de PDF de titel maar één keer (geen dubbele weergave).",
  },
  {
    category: "ai-assistent",
    q: "Kan ik een AI-tekst terugdraaien?",
    a: "Ja. Elke 'Overnemen'-actie maakt een Undo-entry aan. Klik naast het tekstveld op ↶ Ongedaan maken om terug te vallen op je vorige versie. Er worden max 5 versies per veld bewaard.",
  },
  {
    category: "offertes",
    q: "Wat is 'Editor = Preview = PDF'?",
    a: "Dat betekent dat de bedragen die je in de editor ziet, in de Offerte Preview én in de gedownloade PDF identiek zijn. Elke prijs-override, omschrijving-override en meetmethode-keuze telt op alle drie plaatsen exact hetzelfde. Wat je ziet, is wat je klant krijgt.",
  },
  {
    category: "meten",
    q: "Zit Composiet erbij als materiaal?",
    a: "Ja. Voor raamlijsten en dorpels kan je materiaal 'Composiet' kiezen. Beitsen en vernissen worden dan niet aangeboden (die zijn hout-specifiek); wel reinigen, primer en schilderen/lakken.",
  },
  {
    category: "offertes",
    q: "Hoe zorg ik dat mijn wijzigingen niet verloren gaan?",
    a: "Alles wordt automatisch bewaard terwijl je typt. Sluit je het tabblad of laad je opnieuw? Bij het openen zie je de offerte precies zoals je hem verliet, inclusief prijs-overrides en 'AANGEPAST'-badges.",
  },
  {
    category: "offertes",
    q: "Kan ik een offerte dupliceren voor een gelijkaardige klant?",
    a: "Ja. Open de offerte en klik bovenaan op 'Dupliceren'. De kamers, werkzones en geselecteerde werken worden gekopieerd naar een nieuwe offerte. Pas daarna enkel de klant-, project- of meetgegevens aan waar nodig.",
  },
  {
    category: "prijzen",
    q: "Wat is het verschil tussen een sjabloon en een offerte?",
    a: "Een sjabloon is een vooraf samengestelde set werken, bijvoorbeeld 'EMMA™ — Wanden en plafonds spuiten'.\nEen offerte is de volledige prijsberekening voor één concreet project, met jouw ruimtes, afmetingen en gekozen werken.",
  },
  {
    category: "offertes",
    q: "Waarom staan sommige werken op € 0?",
    a: "Een werk staat op € 0 wanneer het niet is aangevinkt of wanneer er nog geen hoeveelheid werd ingevuld (geen aantal treden, lengte of oppervlakte). Niet-geselecteerde werken tellen niet mee in het totaal.",
  },
  {
    category: "offertes",
    q: "Hoe werkt het gemengd BTW-tarief (6% + 21%)?",
    a: "Elke prijscode heeft een eigen BTW-tarief. Onderaan de PDF zie je automatisch een overzicht per gebruikt tarief.\nWil je het BTW-tarief ook bij elke afzonderlijke regel zien, schakel dan 'BTW-percentage tonen per regel' in.",
  },
  {
    category: "offertes",
    q: "Editorbedrag, Preview en PDF — kloppen die altijd met elkaar?",
    a: "Ja. Het bedrag dat je in de editor ziet, in de Offerte Preview én in de gegenereerde PDF is altijd exact hetzelfde. Er is geen verschil tussen wat je ziet en wat je klant krijgt.",
  },
  {
    category: "offertes",
    q: "Kan ik EmmaMeasure™ offline gebruiken?",
    a: "EmmaMeasure werkt het best met een internetverbinding. Volledige offlinewerking en PWA-installatie worden later toegevoegd.",
  },
  {
    category: "instellingen",
    q: "Hoe voeg ik mijn bedrijfslogo toe?",
    a: "Ga naar Instellingen → Bedrijfsinstellingen en upload je eigen logo. Heb je nog geen logo? Kies dan een eenvoudige standaardavatar of gebruik je initialen.",
  },
  {
    category: "prijzen",
    q: "Wat als een sjabloon een werk niet bevat dat ik nodig heb?",
    a: "Open de juiste kamer of werkzone en klik onderaan op 'Code toevoegen'. Kies vervolgens de gewenste prijscode uit je prijslijst. Die extra regel verschijnt apart naast de werken uit het sjabloon.",
  },
  {
    category: "ai-assistent",
    q: "Wat is Emma™?",
    a: "Emma™ is de slimme meet- en offerte-assistent binnen EmmaMeasure™. Ze helpt je relevante sjablonen en werken te kiezen op basis van de ruimte of werkzone die je toevoegt, zoals binnenwerk, gevel, trap of schrijnwerk.",
  },
  // ────────────────────────────────────────────────────────────────
  // v14l — Nieuwe FAQ's over Emma AI, merkvoorkeuren, cover email en SMTP
  // ────────────────────────────────────────────────────────────────
  {
    category: "ai-assistent",
    q: "Wat doet 'Genereer projectomschrijving' precies?",
    a: "Emma leest je geselecteerde werken, ruimtes en de extra context die je zelf invult. Op basis daarvan stelt ze een titel, een korte omschrijving, een 'De werken omvatten'-tekst en opmerkingen/uitsluitingen voor. Ze verzint geen werken, geen prijzen, geen merken. Elk voorstel verschijnt in een preview: jij beslist wat je overneemt.",
  },
  {
    category: "ai-assistent",
    q: "Waarvoor dient 'Extra context voor Emma'?",
    a: "Hierin schrijf je projectspecifieke waarheden die niet uit de meetgegevens blijken. Voorbeelden: 'Behangpapier wordt geleverd door klant', 'Enkel één wand behangen', 'Kleur nog te kiezen door klant'. Emma respecteert die tekst letterlijk als projectwaarheid en verwerkt ze in de omschrijving en e-mail.",
  },
  {
    category: "instellingen",
    q: "Hoe werken merk- en materiaalvoorkeuren?",
    a: "Onder Instellingen → 'Merken & materialen' (aparte sectie) kies je per categorie (binnenverf, buitenverf, lakwerk, buitenschrijnwerk, decoratieve afwerkingen, behang): 'Nooit vermelden' (Emma vermeldt het merk nooit in klantteksten), 'Alleen indien relevant' (enkel als een werk in die categorie is aangevinkt), of 'Standaard vermelden' (Emma verweeft het merk waar het natuurlijk past).",
  },
  {
    category: "ai-assistent",
    q: "Ik heb meerdere merken ingevuld — gebruikt Emma ze allemaal?",
    a: "Nee. Emma gebruikt in klantgerichte teksten uitsluitend het EERSTE merk in het veld. Bijkomende merken (gescheiden door komma, schuine streep, of 'of') worden als reserve bewaard, maar nooit automatisch opgesomd als 'merk A of merk B'. Wil je zelf beide merken vermelden? Voeg dit dan expliciet toe aan 'Extra context voor Emma'.",
  },
  {
    category: "instellingen",
    q: "Wat doet 'Laat Emma een begeleidende e-mail opstellen'?",
    a: "Emma schrijft een korte, warme e-mail bij je offerte — alsof je zelf mailt na een plaatsbezoek. Ze begint waar mogelijk met de voornaam van je klant (bijvoorbeeld 'Beste Sandra,'). Bij een bedrijfsnaam of onduidelijke naam gebruikt ze een neutrale 'Beste,'. Ze verzint geen afspraken, deadlines of prijzen.",
  },
  {
    category: "instellingen",
    q: "Hoe kiest Emma de begroeting in de e-mail?",
    a: "Volgens deze volgorde: (1) staat er een duidelijke voornaam + familienaam, dan → 'Beste {voornaam},'. (2) Bij twijfel of één woord: → 'Beste {volledige naam},'. (3) Bij een bedrijfsnaam of geen bruikbare naam → 'Beste,'. Emma raadt nooit een geslacht of naam die niet in EmmaMeasure staat.",
  },
  {
    category: "instellingen",
    q: "Wat is 'Standaard e-mailinstellingen'?",
    a: "Onder Instellingen → E-mail vul je één keer je afzendernaam, reply-to adres, standaard onderwerp, standaard begeleidend bericht en handtekening in. Deze worden automatisch gebruikt op elke offerte. Plaatsvervangers zoals {project_name}, {quote_number} of {client_name} worden per offerte automatisch ingevuld.",
  },
  {
    category: "instellingen",
    q: "Hoe voeg ik een handtekening toe aan mijn e-mails?",
    a: "Vul de handtekening in onder Instellingen → E-mail → Handtekening. Deze wordt automatisch onderaan elke offerte-e-mail toegevoegd — één keer, zonder dubbels, ook wanneer Emma de e-mail zelf opstelt.",
  },
  {
    category: "instellingen",
    q: "Kan ik de e-mailtekst kopiëren naar mijn eigen mailprogramma?",
    a: "Ja. Op de offerte-detailpagina klik je op 'Kopieer e-mailtekst' om onderwerp + body naar je klembord te kopiëren. Of klik 'Open in mijn mailprogramma' om je standaard mailtool (Mail, Outlook, Gmail-desktop…) te openen met de tekst al ingevuld. De PDF voeg je in dat geval zelf toe als bijlage.",
  },
  {
    category: "instellingen",
    q: "Hoe stuur ik een offerte rechtstreeks vanuit EmmaMeasure?",
    a: "Koppel je mailbox één keer via 'Emma, help mij mijn e-mail koppelen' onder Instellingen → E-mail. Daarna klik je op een offerte gewoon op 'Verzend via mijn mailbox' — Emma stuurt de e-mail met PDF-bijlage rechtstreeks door via jouw eigen adres.",
  },
  {
    category: "instellingen",
    q: "Wat is de Emma-wizard voor e-mailkoppeling?",
    a: "Een eenvoudige stap-voor-stap begeleiding. Je kiest je provider (Gmail, Outlook, iCloud of Andere) en Emma vult automatisch de technische SMTP-gegevens in. Je hoeft alleen je e-mailadres + wachtwoord (of app-wachtwoord) in te geven. De wizard legt per provider uit waar je dat wachtwoord vindt.",
  },
  {
    category: "instellingen",
    q: "Waar vind ik een app-wachtwoord voor Gmail?",
    a: "Bij Google → 'App-wachtwoorden' (myaccount.google.com/apppasswords). Voorwaarde: 2-staps-verificatie moet aan staan. Kies 'Mail' + 'Andere' → typ 'EmmaMeasure'. Google toont een code van 16 letters — kopieer die en plak hem in de wizard. Je gewone Gmail-wachtwoord werkt niet voor SMTP.",
  },
  {
    category: "instellingen",
    q: "Waar vind ik een app-wachtwoord voor iCloud (@me.com of @icloud.com)?",
    a: "Ga naar appleid.apple.com → 'Aanmelden en beveiliging' → 'App-specifieke wachtwoorden' → '+ Genereer'. Geef als label 'EmmaMeasure'. Apple toont een code in de vorm xxxx-xxxx-xxxx-xxxx — kopieer die volledig (met streepjes). Voor iCloud is dit altijd nodig; je Apple ID-wachtwoord werkt niet.",
  },
  {
    category: "instellingen",
    q: "Waar vind ik het wachtwoord voor Outlook of Microsoft 365?",
    a: "Voor persoonlijke accounts met 2-staps-verificatie: maak een app-wachtwoord aan via account.microsoft.com/security → 'Geavanceerde beveiligingsopties'. Zonder 2-staps-verificatie: je gewone Outlook-wachtwoord werkt. Voor zakelijke Microsoft 365-accounts: het kan zijn dat je IT-beheerder SMTP-verzending eerst moet inschakelen — Emma raadt hier niet.",
  },
  {
    category: "instellingen",
    q: "Wat is 'Test verbinding'?",
    a: "Na het opslaan van je mailbox-koppeling klikt de wizard automatisch op 'Test verbinding' om zeker te zijn dat Emma een e-mail kan versturen via jouw adres. Lukt de test niet, dan krijg je een korte, begrijpelijke uitleg (bv. 'Aanmelden mislukt — controleer je wachtwoord'). Geen technische foutcodes.",
  },
  {
    category: "instellingen",
    q: "Kan ik de mailbox-koppeling later verbreken?",
    a: "Ja. Ga naar Instellingen → E-mail → open 'Geavanceerde instellingen' → klik 'Verbinding verbreken'. Je opgeslagen wachtwoord wordt daarbij permanent verwijderd. Je kan altijd opnieuw koppelen via de Emma-wizard.",
  },
  {
    category: "instellingen",
    q: "Is mijn wachtwoord veilig opgeslagen?",
    a: "Ja. Je wachtwoord of app-wachtwoord wordt versleuteld opgeslagen (Fernet-encryptie) en is na opslaan niet opnieuw zichtbaar — ook niet voor jou. Bij verbreken wordt het permanent verwijderd. EmmaMeasure logt of toont nooit een leesbaar wachtwoord.",
  },
  {
    category: "instellingen",
    q: "Wat als mijn klant geen e-mailadres heeft?",
    a: "Geen probleem. Op de offerte-pagina toont de e-mail-sectie dan een korte hint 'klant heeft nog geen e-mailadres — voeg dit toe onder Klanten om te kunnen versturen'. Je kan wél altijd de PDF downloaden en op papier of via een ander kanaal bezorgen.",
  },
  {
    category: "instellingen",
    q: "Wordt de PDF automatisch bijgevoegd als ik rechtstreeks verzend?",
    a: "Ja. Bij rechtstreeks verzenden via je gekoppelde mailbox voegt EmmaMeasure de offerte-PDF automatisch toe als bijlage. Bij 'Open in mijn mailprogramma' voeg je de PDF zelf toe (die staat kant-en-klaar in je Downloads).",
  },
  // ────────────────────────────────────────────────────────────────
  // v6 F35-refresh — Nieuwe FAQ's: Facturen, BTW, Internationaal
  // ────────────────────────────────────────────────────────────────
  {
    category: "facturen",
    q: "Waarom heeft mijn concept-factuur nog geen nummer?",
    a: "Een factuurnummer wordt pas toegekend wanneer je op 'Definitief maken' klikt. Zo blijft je nummering ononderbroken, ook als je een concept later verwijdert of aanpast. Zolang de factuur concept is zie je enkel de status 'Concept'.",
  },
  {
    category: "facturen",
    q: "Wat is het verschil tussen een voorschot-, tussen- en eindfactuur?",
    a: "Voorschotfactuur: een deel vóór start van de werken. Tussenfactuur: tijdens de uitvoering, voor een tussenfase. Eindfactuur: sluit het project af en factureert altijd exact het resterend factureerbaar saldo — je krijgt dus nooit overfacturatie of restsaldo, ongeacht welk percentage je invulde.",
  },
  {
    category: "facturen",
    q: "Kan ik een vrije factuur maken zonder gekoppelde offerte?",
    a: "Ja. Ga naar Facturen → 'Nieuwe vrije factuur'. Kies je klant en voeg de gewenste lijnen toe. Het BTW-regime wordt automatisch overgenomen van de klant, maar je kan het per factuur nog aanpassen zolang de factuur concept is.",
  },
  {
    category: "facturen",
    q: "Kan ik starten vanaf een bestaand factuurnummer, bijvoorbeeld FAC-2026-127?",
    a: "Ja. Ga naar Instellingen → Facturen & betalingen → Factuurnummering en zet 'Volgend nummer' op 127. Zorg dat je voorvoegsel en de jaartal-optie overeenkomen met je bestaande boekhouding. Bovenaan verschijnt live een preview van de eerstvolgende factuur.",
  },
  {
    category: "facturen",
    q: "Kan ik een definitieve factuur nog wijzigen?",
    a: "Nee. Eens definitief wordt de factuur bevroren: bedragen, BTW-regime en wettelijke tekst kunnen niet meer aangepast worden. Zo blijft de factuur juridisch stabiel. Wil je toch corrigeren? Maak een aparte creditnota aan (functionaliteit volgt).",
  },
  {
    category: "facturen",
    q: "Waar vind ik mijn IBAN, BIC en betalingsreferentie op de factuur?",
    a: "IBAN en BIC stel je één keer in onder Instellingen → Facturen & betalingen → Betalingsgegevens. Ze verschijnen automatisch op elke factuur. De betalingsreferentie wordt per factuur ingevuld op basis van je sjabloon (bv. 'Factuur FAC-2026-127').",
  },
  {
    category: "btw",
    q: "Welk BTW-regime moet ik kiezen?",
    a: "Standaard BTW: gewone tarieven per lijn (21% / 6%). Renovatie 6%: verlaagd tarief voor werken aan een privéwoning die aan de wettelijke voorwaarden voldoet. BTW verlegd: BTW naar je opdrachtgever verlegd. Twijfel je? Overleg met je accountant — Emma geeft geen fiscaal advies.",
  },
  {
    category: "btw",
    q: "Waar stel ik het standaard BTW-regime van een klant in?",
    a: "Onder 'Klanten' → klant openen → veld 'Standaard BTW-regime'. Elke nieuwe factuur voor die klant erft automatisch dit regime. Je kan het per factuur nog wijzigen zolang de factuur concept is.",
  },
  {
    category: "btw",
    q: "Wat betekent 'Wettelijke tekst nog te bevestigen door je accountant'?",
    a: "Voor Renovatie 6% en BTW verlegd toont EmmaMeasure een korte juridische vermelding op de factuur. Zolang je accountant deze tekst niet formeel bevestigd heeft, wordt duidelijk aangegeven dat de definitieve wettelijke formulering nog volgt. Je BTW-berekening verandert daardoor niet — enkel de vermelding zelf.",
  },
  {
    category: "internationaal",
    q: "Welke landen ondersteunt EmmaMeasure?",
    a: "Vijf landen: 🇧🇪 België, 🇳🇱 Nederland, 🇱🇺 Luxemburg, 🇫🇷 Frankrijk en 🇩🇪 Duitsland. Kies je land onder Instellingen → Jouw bedrijf. Voor Luxemburg, Frankrijk en Duitsland is de fiscale configuratie voorlopig nog beperkt — enkel 'Standaard BTW' is actief.",
  },
  {
    category: "internationaal",
    q: "Kan ik in EmmaMeasure Franse of Duitse offertes maken voor Belgische klanten?",
    a: "Ja. België ondersteunt Nederlands, Frans en Duits. Ga naar Instellingen → Jouw bedrijf → Documenttaal & regio en kies 'Français (Belgique)' of 'Deutsch (Belgien)'. Je documenten en labels passen automatisch mee.",
  },
  {
    category: "internationaal",
    q: "Wat is het verschil tussen 'Land' en 'Taal & regio'?",
    a: "Land bepaalt de fiscale context: welke BTW-regimes beschikbaar zijn en welke registratienummers (bv. KvK voor NL, RCS voor LU, SIREN/SIRET voor FR). Taal & regio bepaalt hoe je documenten getoond worden: taal van labels, datum- en getalnotatie.",
  },

];

export default function Help() {
  const [openTopic, setOpenTopic] = useState(null);
  const [openFaq, setOpenFaq] = useState(null);
  const [q, setQ] = useState("");
  const query = q.trim().toLowerCase();

  const filteredTopics = query
    ? TOPICS.filter((t) => t.title.toLowerCase().includes(query) || t.desc.toLowerCase().includes(query) || t.steps.some((s) => s.toLowerCase().includes(query)))
    : TOPICS;
  const filteredFaq = query
    ? FAQ.filter((f) => f.q.toLowerCase().includes(query) || f.a.toLowerCase().includes(query))
    : FAQ;

  return (
    <div className="max-w-4xl mx-auto p-4 sm:p-6 space-y-5">
      <div className="flex items-center gap-3">
        <Link to="/" className="w-10 h-10 rounded-full border border-[#E5E2DC] hover:bg-[#F0EFEA] inline-flex items-center justify-center flex-shrink-0" data-testid="help-back-btn">
          <ArrowLeft className="w-4 h-4" />
        </Link>
        <div className="flex-1 min-w-0">
          <h1 className="font-heading text-4xl sm:text-5xl font-bold flex items-center gap-3 text-[#2C2C2A] leading-tight">
            <BookOpen className="w-7 h-7 sm:w-9 sm:h-9 text-[#D96C4A] flex-shrink-0" /> Help &amp; handleiding
          </h1>
          <p className="text-sm text-[#4F4F4D] mt-0.5">Snel wegwijs in EmmaMeasure™</p>
        </div>
      </div>

      {/* Zoekbalk */}
      <div className="relative">
        <Search className="w-4 h-4 text-[#A8A7A0] absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
        <Input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Zoek in handleiding en veelgestelde vragen..."
          className="h-11 rounded-xl pl-9"
          data-testid="help-search"
        />
      </div>

      {/* v6 F35-refresh — Topics + FAQ per categorie (gewone koppen, geen sticky navigatie).
          Elke categorie toont eerst haar topics, dan haar FAQ's. Verborgen als leeg
          (bv. tijdens zoekfilter). */}
      {filteredTopics.length === 0 && filteredFaq.length === 0 && query && (
        <p className="text-sm text-[#4F4F4D] italic" data-testid="help-no-results">
          Geen resultaten gevonden voor &quot;{q}&quot;.
        </p>
      )}
      {CATEGORIES.map((cat) => {
        const catTopics = filteredTopics.filter((t) => t.category === cat.id);
        const catFaqs   = filteredFaq.filter((f) => f.category === cat.id);
        if (catTopics.length === 0 && catFaqs.length === 0) return null;
        return (
          <section key={cat.id} className="space-y-2" data-testid={`help-section-${cat.id}`}>
            <h2 className="font-heading text-2xl font-bold text-[#2C2C2A] pt-2 border-t border-[#F0EFEA] first:border-t-0">
              {cat.label}
            </h2>

            {catTopics.map((t) => {
              const isOpen = openTopic === t.id;
              const Icon = t.icon;
              return (
                <div key={t.id} className="emma-card !p-0 overflow-hidden" data-testid={`help-topic-${t.id}`}>
                  <button
                    onClick={() => setOpenTopic(isOpen ? null : t.id)}
                    className="w-full flex items-start gap-3 px-4 py-3 text-left hover:bg-[#FAF9F6]"
                    data-testid={`help-topic-toggle-${t.id}`}
                  >
                    <ChevronDown className={`w-4 h-4 mt-1 text-[#D96C4A] transition-transform flex-shrink-0 ${isOpen ? "" : "-rotate-90"}`} />
                    <Icon className="w-5 h-5 mt-0.5 text-[#5D7259] flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold text-[#2C2C2A]">{t.title}</div>
                      <div className="text-xs text-[#4F4F4D] mt-0.5 leading-snug">{t.desc}</div>
                    </div>
                  </button>
                  {isOpen && (
                    <div className="border-t border-[#F0EFEA] px-4 py-3 bg-[#FDFCFA]">
                      <ol className="list-decimal list-outside pl-5 space-y-2 text-sm text-[#2C2C2A] leading-relaxed">
                        {t.steps.map((s, i) => <li key={i}>{s}</li>)}
                      </ol>
                    </div>
                  )}
                </div>
              );
            })}

            {catFaqs.map((f) => {
              const idx = FAQ.indexOf(f);
              const key = `faq-${idx}`;
              const isOpen = openFaq === key;
              return (
                <div key={key} className="emma-card !p-0 overflow-hidden" data-testid={`help-faq-${idx}`}>
                  <button
                    onClick={() => setOpenFaq(isOpen ? null : key)}
                    className="w-full flex items-start gap-3 px-4 py-3 text-left hover:bg-[#FAF9F6]"
                    data-testid={`help-faq-toggle-${idx}`}
                  >
                    <ChevronDown className={`w-4 h-4 mt-1 text-[#D96C4A] transition-transform flex-shrink-0 ${isOpen ? "" : "-rotate-90"}`} />
                    <HelpCircle className="w-4 h-4 mt-1 text-[#D96C4A]/70 flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-[#2C2C2A]">{f.q}</div>
                    </div>
                  </button>
                  {isOpen && (
                    <div className="border-t border-[#F0EFEA] px-4 py-3 bg-[#FDFCFA]">
                      <p className="text-sm text-[#2C2C2A] leading-relaxed whitespace-pre-line">{f.a}</p>
                    </div>
                  )}
                </div>
              );
            })}
          </section>
        );
      })}

      {/* v6 F13 §8 — Volledige handleiding downloaden (voorheen "Uitgebreide gids").
          Publieke gebruikerscopy — geen bèta-tester verwijzingen meer. */}
      <div className="emma-card flex items-start gap-3 border border-[#F4D6C4] bg-[#FDF7F2]" data-testid="help-legacy-pdf">
        <FileDown className="w-5 h-5 text-[#D96C4A] mt-0.5 flex-shrink-0" />
        <div className="flex-1 min-w-0">
          <div className="font-semibold text-[#2C2C2A]">Volledige handleiding downloaden</div>
          <p className="text-sm text-[#4F4F4D] mt-0.5 leading-snug">
            Praktische uitleg met screenshots voor iPhone, iPad en Android.
          </p>
        </div>
        <a
          href={`${process.env.REACT_APP_BACKEND_URL}/api/handleiding/pdf`}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="help-pdf-download"
          className="rounded-xl h-10 px-4 border border-[#D96C4A] text-[#D96C4A] hover:bg-[#D96C4A] hover:text-white flex-shrink-0 inline-flex items-center justify-center text-sm font-medium transition-colors gap-2"
        >
          <FileDown className="w-4 h-4" /> PDF
        </a>
      </div>
    </div>
  );
}
